import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  CheckCircle as ValidatedIcon,
  Send as SentIcon,
  Payment as PaidIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as CreateIcon,
  Warning as OverdueIcon,
  Receipt as CreditNoteIcon,
  Discount as DiscountIcon,
  NotificationsActive as ReminderIcon,
} from '@mui/icons-material';
import {
  useInvoiceHistory,
  useRecentInvoiceActivity,
  useInvoices,
} from '../../hooks/useBilling';
import SmartDataGrid from '../common/SmartDataGrid';
import { handleApiError } from '../../utils/errorHandler'; // eslint-disable-line no-unused-vars
import {
  SUPPORTED_DATE_FORMATS,
  getDateFormatPreview,
} from '../../utils/invoice-pdf-general-section-utilities';

const InvoiceHistoryPanel = () => {
  const { t, i18n: _i18n } = useTranslation('business');
  const theme = useTheme();
  const { _enqueueSnackbar } = useSnackbar();

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [selectedDateFormat, setSelectedDateFormat] = useState('DD/MM/YYYY');

  // Handle date format change
  const handleDateFormatChange = useCallback((event) => {
    setSelectedDateFormat(event.target.value);
  }, []);

  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices();
  const { data: historyData, isLoading: historyLoading, refetch: refetchHistory } = useInvoiceHistory(selectedInvoice?.id);
  const { data: recentActivityData, isLoading: recentLoading } = useRecentInvoiceActivity({ limit: 50 });

  const invoices = useMemo(() => invoicesData?.items || invoicesData || [], [invoicesData]);
  const history = useMemo(() => historyData?.items || historyData || [], [historyData]);
  const recentActivity = useMemo(() => recentActivityData?.items || recentActivityData || [], [recentActivityData]);

  const filteredHistory = useMemo(() => {
    let filtered = selectedInvoice ? history : recentActivity;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.action?.toLowerCase().includes(query) ||
        entry.user_name?.toLowerCase().includes(query) ||
        entry.data?.toLowerCase().includes(query)
      );
    }
    if (actionFilter) {
      filtered = filtered.filter(entry => entry.action === actionFilter);
    }
    return filtered;
  }, [history, recentActivity, selectedInvoice, searchQuery, actionFilter]);

  const getActionIcon = (action) => {
    const icons = {
      created: <CreateIcon />,
      validated: <ValidatedIcon />,
      sent: <SentIcon />,
      paid: <PaidIcon />,
      updated: <EditIcon />,
      deleted: <DeleteIcon />,
      overdue: <OverdueIcon />,
      credit_note: <CreditNoteIcon />,
      discount_applied: <DiscountIcon />,
      reminder_sent: <ReminderIcon />,
    };
    return icons[action?.toLowerCase()] || <HistoryIcon />;
  };

  const getActionColor = (action) => {
    const colorMap = {
      created: theme.palette.primary.main,
      validated: theme.palette.info.main,
      sent: theme.palette.secondary.main,
      paid: theme.palette.success.main,
      updated: theme.palette.warning.main,
      deleted: theme.palette.error.main,
      overdue: theme.palette.error.main,
      credit_note: theme.palette.warning.main,
      discount_applied: theme.palette.info.main,
      reminder_sent: theme.palette.warning.main,
    };
    return colorMap[action?.toLowerCase()] || theme.palette.grey[500];
  };

  const getChipColor = (action) => {
    const colors = {
      created: 'primary',
      validated: 'info',
      sent: 'secondary',
      paid: 'success',
      updated: 'warning',
      deleted: 'error',
      overdue: 'error',
      credit_note: 'warning',
      discount_applied: 'info',
      reminder_sent: 'warning',
    };
    return colors[action?.toLowerCase()] || 'default';
  };

  // Format date using selected date format from invoice PDF utilities
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return getDateFormatPreview(selectedDateFormat, date);
  }, [selectedDateFormat]);

  const invoiceColumns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'invoice_number', headerName: t('invoicing.history.columns.invoiceNumber'), flex: 1 },
    { 
      field: 'status', 
      headerName: t('invoicing.history.columns.status'), 
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color={params.value === 'paid' ? 'success' : 'default'} />
      ),
    },
    { 
      field: 'customer_name', 
      headerName: t('invoicing.history.columns.customer'), 
      flex: 1,
    },
  ];

  const historyColumns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { 
      field: 'action', 
      headerName: t('invoicing.history.columns.action'), 
      width: 150,
      renderCell: (params) => (
        <Chip 
          icon={getActionIcon(params.value)}
          label={t(`invoicing.history.actions.${params.value?.toLowerCase()}`, params.value)} 
          size="small" 
          color={getActionColor(params.value)}
          variant="outlined"
        />
      ),
    },
    { 
      field: 'user_name', 
      headerName: t('invoicing.history.columns.user'), 
      width: 150,
    },
    { 
      field: 'created_at', 
      headerName: t('invoicing.history.columns.timestamp'), 
      width: 180,
      renderCell: (params) => formatDate(params.value),
    },
    { 
      field: 'data', 
      headerName: t('invoicing.history.columns.details'), 
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value || '-'}
        </Typography>
      ),
    },
  ];

  const actionTypes = ['created', 'validated', 'sent', 'paid', 'updated', 'deleted', 'overdue', 'credit_note', 'discount_applied', 'reminder_sent'];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">{t('business.invoicing.history.title', 'Invoice History')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('business.invoicing.history.subtitle', 'Track invoice changes and audit trail')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder={t('business.invoicing.history.searchPlaceholder', 'Search history...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('business.invoicing.history.filterByAction', 'Filter by Action')}</InputLabel>
            <Select
              value={actionFilter}
              label={t('business.invoicing.history.filterByAction', 'Filter by Action')}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all', 'All')}</MenuItem>
              {actionTypes.map(action => (
                <MenuItem key={action} value={action}>
                  {t(`business.invoicing.history.actions.${action}`, action)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('common.dateFormat', 'Date Format')}</InputLabel>
            <Select
              value={selectedDateFormat}
              label={t('common.dateFormat', 'Date Format')}
              onChange={handleDateFormatChange}
            >
              {SUPPORTED_DATE_FORMATS.map(format => (
                <MenuItem key={format.value} value={format.value}>
                  {format.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetchHistory()}>
            {t('common.refresh', 'Refresh')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, display: 'flex' }}>
        <Box sx={{ width: selectedInvoice ? '35%' : '100%', borderRight: selectedInvoice ? 1 : 0, borderColor: 'divider' }}>
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
            <Typography variant="subtitle2">
              {selectedInvoice ? t('business.invoicing.history.selectDifferentInvoice', 'Select a different invoice') : t('business.invoicing.history.selectInvoice', 'Select an invoice to view history')}
            </Typography>
          </Box>
          <SmartDataGrid
            rows={invoices}
            columns={invoiceColumns}
            loading={invoicesLoading}
            onRowClick={(params) => setSelectedInvoice(params.row)}
            getRowClassName={(params) => params.row.id === selectedInvoice?.id ? 'Mui-selected' : ''}
          />
        </Box>

        {selectedInvoice && (
          <Box sx={{ width: '65%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle1">
                    {t('business.invoicing.history.historyFor', 'History for')} {selectedInvoice.invoice_number || `INV-${selectedInvoice.id}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filteredHistory.length} {t('business.invoicing.history.entries', 'entries')}
                  </Typography>
                </Box>
                <Button size="small" onClick={() => setSelectedInvoice(null)}>
                  {t('business.invoicing.history.viewAllActivity', 'View All Activity')}
                </Button>
              </Box>
            </Box>
            
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredHistory.length === 0 ? (
              <Alert severity="info" sx={{ m: 2 }}>{t('business.invoicing.history.messages.noHistoryFound', 'No history found for this invoice')}</Alert>
            ) : (
              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                <List>
                  {filteredHistory.map((entry, index) => (
                    <React.Fragment key={entry.id || index}>
                      <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                        <ListItemIcon sx={{ minWidth: 56 }}>
                          <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Avatar 
                              sx={{ 
                                bgcolor: getActionColor(entry.action), 
                                width: 40, 
                                height: 40,
                              }}
                            >
                              {getActionIcon(entry.action)}
                            </Avatar>
                            {index < filteredHistory.length - 1 && (
                              <Box 
                                sx={{ 
                                  width: 2, 
                                  height: 40, 
                                  bgcolor: 'divider', 
                                  mt: 1,
                                }} 
                              />
                            )}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip 
                                label={t(`business.invoicing.history.actions.${entry.action?.toLowerCase()}`, entry.action)} 
                                size="small" 
                                color={getChipColor(entry.action)}
                                variant="outlined"
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(entry.created_at)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {t('business.invoicing.history.byUser', 'By')}: {entry.user_name || 'System'}
                              </Typography>
                              {entry.data && (
                                <Paper variant="outlined" sx={{ p: 1, mt: 1, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                                  <Typography variant="caption">
                                    {entry.data}
                                  </Typography>
                                </Paper>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < filteredHistory.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}

        {!selectedInvoice && (
          <Box sx={{ width: '100%', display: 'none' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Typography variant="subtitle1">{t('business.invoicing.history.recentActivity', 'Recent Activity')}</Typography>
            </Box>
            {recentLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <SmartDataGrid
                rows={filteredHistory}
                columns={historyColumns}
                loading={recentLoading}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(InvoiceHistoryPanel);
